<template>
  <div
    class="rs-switch"
    :class="{
      'label--right': labelPosition == 'right',
      'label--left': labelPosition == 'left',
      'label--top': labelPosition == 'top',
      'label--bottom': labelPosition == 'bottom',
    }"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
  >
    <label
      :for="`switch_${id}`"
      class="rs-switch__label"
    >
      <span
        v-if="label"
        class="label"
        :class="labelClass"
      >
        {{ label }}
        <span v-if="helpTooltip">
          <RsIcon
            :id="`helperTooltip-${id}`"
            icon="question-circle"
            class="tooltip-question-icon"
          />
          <RsTooltip
            :target="`helperTooltip-${id}`"
            :title="helpTooltip"
            :position="helpTooltipPosition"
            :delay="{ show: 200 }"
          />
        </span>
      </span>
      <input
        :id="`switch_${id}`"
        type="checkbox"
        class="rs-switch__input"
        :disabled="disabled ? true : false"
        :checked="shouldBeChecked"
        :value="value"
        :name="name"
        @change="updateInput"
      >
      <div
        v-b-tooltip.top="disabled && disabledTooltip ? disabledTooltip : ''"
        class="rs-switch__button"
      />
    </label>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsFormSwitch',
  model: {
    prop: 'modelValue',
    event: 'change',
  },
  props: {
    id: [Number, String],
    label: String,
    labelPosition: {
      type: String,
      default: 'left',
    },
    name: String,
    checked: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Object, Number, String, Boolean],
    },
    modelValue: {
      type: [Boolean, Number, String, Array],
      default: false,
    },
    trueValue: {
      type: [Boolean, Number, String],
      default: true,
    },
    falseValue: {
      type: [Boolean, Number, String],
      default: false,
    },
    labelClass: {
      type: String,
      default: '',
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String,
      default: 'top',
      validator: (value: string) => ['top', 'right', 'bottom', 'left'].includes(value),
    },
    disabledTooltip: {
      type: String,
      default: '',
    },
  },
  computed: {
    shouldBeChecked(): boolean {
      if (this.modelValue instanceof Array) {
        return this.modelValue.some((v: any) => {
          if (v.id) {
            return v.id === this.value.id;
          }
          return v === this.value;
        });
      }
      return this.modelValue === this.trueValue || false;
    },
  },
  methods: {
    updateInput(event: Event): void {
      const isChecked = (event.target as HTMLInputElement)?.checked;

      if (this.modelValue instanceof Array) {
        const newValue = [...this.modelValue];

        if (isChecked) {
          newValue.push(this.value);
        } else {
          newValue.splice(newValue.indexOf(this.value), 1);
        }

        this.$emit('change', newValue);
      } else {
        this.$emit('change', isChecked ? this.trueValue : this.falseValue);
      }
    },
    onMouseOver(event: Event): void {
      this.$emit('mouseover', event, this.id);
    },
    onMouseLeave(event: Event): void {
      this.$emit('mouseleave', event, this.id);
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.rs-switch{
  $switch-height: 20px !default;
  $switch-border-radius: $switch-height !default;
  position: relative;
  margin-bottom: 0.5rem;
  &__label{
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    line-height: $switch-height;
    &:hover{
      cursor: pointer;
    }
  }
  &.label--right{
    .rs-switch__label{
      flex-direction: row-reverse;
      .label{
        margin: 0 0 0 1rem;
      }
    }
  }
  &.label--top{
    .rs-switch__label{
      flex-direction: column;
      .label{
        margin: auto;
      }
    }
  }
  &.label--bottom{
    .rs-switch__label{
      flex-direction: column-reverse;
      .label{
        margin: auto;
      }
    }
  }
  .label{
    display: inline-block;
    margin: 0 1rem 0 0;
    line-height: $switch-height;
  }
  &__input{
    position: absolute;
    left: 0;
    z-index: -1;
    width: 1rem;
    height: 1.25rem;
    opacity: 0;
    &:checked + .rs-switch__button{
      background-color: $success;
      &:before{
        content: "";
        position: absolute;
        display: block;
        width: 9px;
        height: 5px;
        top: 9px;
        left: 10px;
        transform: translate3d(-50%,-50%,0) rotate(-45deg);
        border-bottom: 2px solid $white;
        border-left: 2px solid $white;
      }
      &:after{
        content: "";
        left: calc(100% - 18px);
      }
    }
    &:disabled + .rs-switch__button{
      background-color: $tertiary-dark-gray;
      &:after{
        background-color: rgba(0,0,0,0.2);
      }
    }
  }
  &__button{
    content: "";
    position: relative;
    display: inline-block;
    margin: 0;
    width: 34px;
    height: $switch-height;
    border-radius: $switch-border-radius;
    cursor: pointer;
    background: $tertiary-gray;
    transition: background-color .1s ease;
    &:after{
      content: "";
      position: absolute;
      display: block;
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      transition: left .1s ease;
      background-color: $white;
      border-radius: 50%;
    }
  }

  .custom-control{
    .custom-control-label{
      display: none;
    }
  }
}
</style>
